import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { LoaderService } from 'src/app/loader.service';
import { AlertDialogService } from 'src/app/shared/alert-dialog/alert-dialog.service';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import { SharedService } from 'src/app/shared/shared.service';
import { SubscriptionService } from '../../subscription.service';
import { ruleTypeLabel as labelForRuleType } from '../../subscription.applicable-types';
import { environment } from 'src/environments/environment';
import { subscriptionCreateSuccessAlert, subscriptionOperationFailureAlert } from '../../subscription.create-alert';

export interface PostpaidContext {
  state: { id: string, name: string };
  city: { id: string, name: string };
  mode: { id: string, name: string };
  category: string;
  categoryServices: { id: string, name: string, serviceDetailId: string, active?: boolean }[];
  excludableServices: { id: string, name: string, serviceDetailId: string, active?: boolean }[];
  displayTitles?: string[];
  exclusiveOfTax?: boolean;
  eligibleRuleTypes?: string[];
}

@Component({
  selector: 'app-create-postpaid',
  templateUrl: './postpaid.create.component.html',
  styleUrls: ['./postpaid.create.component.css']
})
export class CreatePostpaidComponent implements OnInit, OnChanges {
  @Input() context: PostpaidContext = null;

  public postpaidFormGroup = new FormGroup({
    ruleName: new FormControl(null),
    ruleTitle: new FormControl(null),
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    excludedServices: new FormControl([]),
    targetSegment: new FormControl('entireCity'),
    priority: new FormControl(0),
    userSelector: new FormControl(null),
    ruleType: new FormControl('zeroCommission'),
    rules: new FormArray([])
  });

  public validityTypes: string[] = ['earnings', 'rides', 'unlimited'];
  public cashbackTypes: string[] = ['none', 'rides'];
  public eligibleRuleTypes: string[] = ['zeroCommission'];
  public userSelectors: { id: string, name: string }[] = [];
  public today: Date = moment().startOf('hour').toDate();

  get subscriptionTitles() {
    const titles = this.context && this.context.displayTitles;
    return Array.isArray(titles) ? titles : [];
  }

  get taxInfoText() {
    return this.context && this.context.exclusiveOfTax
      ? 'This amount is exclusive of tax'
      : 'This amount is inclusive of tax';
  }

  constructor(
    private subsService: SubscriptionService,
    private sharedService: SharedService,
    private loader: LoaderService,
    private alertService: AlertDialogService,
    public toasterService: ToasterService
  ) { }

  ngOnInit() {
    this.addRule();
    this.postpaidFormGroup.get('targetSegment').valueChanges.subscribe(() => {
      this.applyTargetSegment();
      this.syncValidityForTargetSegment();
    });
    this.applyTargetSegment();
    this.syncValidityForTargetSegment();
    this.applyEligibleRuleTypes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['context']) {
      this.applyEligibleRuleTypes();
      const previous = changes['context'].previousValue as PostpaidContext;
      const current = changes['context'].currentValue as PostpaidContext;
      if (this.showUserSelector && this.selectorContextKey(previous) !== this.selectorContextKey(current)) {
        this.postpaidFormGroup.get('userSelector').setValue(null, { emitEvent: false });
        this.fetchSelectors();
      }
    }
  }

  private selectorContextKey(context: PostpaidContext) {
    if (!context || !context.city) {
      return '';
    }
    return context.city.id + ':' + (context.categoryServices || []).map(service => service.serviceDetailId).join(',');
  }

  ruleTypeLabel(type: string) {
    return labelForRuleType(type);
  }

  get isRuleTypeLocked() {
    return this.eligibleRuleTypes.length === 1;
  }

  private applyEligibleRuleTypes() {
    const types = (this.context && this.context.eligibleRuleTypes) || [];
    this.eligibleRuleTypes = types.length ? types : ['zeroCommission'];
    const current = this.postpaidFormGroup.get('ruleType').value;
    if (this.eligibleRuleTypes.indexOf(current) === -1) {
      this.postpaidFormGroup.get('ruleType').setValue(this.eligibleRuleTypes[0], { emitEvent: false });
    }
    if (this.eligibleRuleTypes.length === 1) {
      this.postpaidFormGroup.get('ruleType').disable({ emitEvent: false });
    } else {
      this.postpaidFormGroup.get('ruleType').enable({ emitEvent: false });
    }
  }

  get isFemaleGender() {
    return this.formValue('targetSegment') === 'femaleGender';
  }

  get showUserSelector() {
    return this.formValue('targetSegment') === 'userSelectors';
  }

  get isCityWideSegment() {
    const segment = this.formValue('targetSegment');
    return segment === 'entireCity' || segment === 'femaleGender';
  }

  applyTargetSegment() {
    const segment = this.formValue('targetSegment') || 'entireCity';
    const priority = this.postpaidFormGroup.get('priority');
    const userSelector = this.postpaidFormGroup.get('userSelector');

    if (segment === 'userSelectors') {
      priority.enable({ emitEvent: false });
      userSelector.enable({ emitEvent: false });
      this.fetchSelectors();
      return;
    }

    priority.setValue(0, { emitEvent: false });
    priority.disable({ emitEvent: false });
    userSelector.setValue(null, { emitEvent: false });
    userSelector.disable({ emitEvent: false });
  }

  fetchSelectors() {
    if (!this.context || !this.context.city || !this.context.categoryServices || !this.context.categoryServices.length) {
      this.userSelectors = [];
      return;
    }
    const serviceTypes = this.context.categoryServices
      .map(service => service.serviceDetailId)
      .join(',');
    this.loader.openLoading();
    this.sharedService
      .fetchRules(this.context.city.id, serviceTypes, null, 'rider')
      .toPromise()
      .then((res: any) => {
        this.userSelectors = (res.data.selectors || [])
          .map(({ _id, name }) => ({ id: _id, name }));
      })
      .finally(() => this.loader.closeLoading());
  }

  get rules() {
    return this.postpaidFormGroup.get('rules') as FormArray;
  }

  public maxRulesAtOnce: number = environment.maxRulesCreationAtOnce;

  createRule() {
    const rule = new FormGroup({
      validityType: new FormControl('earnings'),
      maxValue: new FormControl(null),
      amount: new FormControl(null),
      periodInDays: new FormControl(null),
      cashbackType: new FormControl('none'),
      cashbackMinValue: new FormControl(null)
    });
    rule.get('amount').valueChanges.subscribe(() => this.syncCashbackForAmount(rule));
    rule.get('validityType').valueChanges.subscribe(() => this.syncMaxValueForValidity(rule));
    return rule;
  }

  get canAddRule() {
    return !this.isFemaleGender && this.rules.length < this.maxRulesAtOnce;
  }

  addRule() {
    if (this.rules.length && !this.canAddRule) { return; }
    this.rules.push(this.createRule());
  }

  deleteRule(index: number) {
    this.rules.removeAt(index);
  }

  syncValidityForTargetSegment() {
    this.validityTypes = this.isFemaleGender ? ['unlimited'] : ['earnings', 'rides', 'unlimited'];
    if (this.isFemaleGender) {
      while (this.rules.length > 1) { this.rules.removeAt(this.rules.length - 1); }
      this.rules.controls.forEach(rule => rule.get('validityType').setValue('unlimited', { emitEvent: false }));
    }
    this.rules.controls.forEach(rule => this.syncMaxValueForValidity(rule));
  }

  isPaidTargetValidity(rule: AbstractControl) {
    const type = rule.get('validityType').value;
    return type === 'rides' || (type === 'unlimited' && !this.isFemaleGender);
  }

  isPaidUnlimited(rule: AbstractControl) {
    return rule.get('validityType').value === 'unlimited' && !this.isFemaleGender;
  }

  syncMaxValueForValidity(rule: AbstractControl) {
    const maxValue = rule.get('maxValue');
    const amount = rule.get('amount');
    const periodInDays = rule.get('periodInDays');
    if (this.isFemaleGender && rule.get('validityType').value === 'unlimited') {
      maxValue.setValue(null, { emitEvent: false });
      maxValue.disable({ emitEvent: false });
      amount.setValue(0);
      amount.disable({ emitEvent: false });
      periodInDays.setValue(null, { emitEvent: false });
      this.syncCashbackForAmount(rule);
      return;
    }
    if (this.isPaidUnlimited(rule)) {
      maxValue.setValue(null, { emitEvent: false });
      maxValue.disable({ emitEvent: false });
      amount.enable({ emitEvent: false });
      this.syncCashbackForAmount(rule);
      return;
    }
    maxValue.enable({ emitEvent: false });
    amount.enable({ emitEvent: false });
    periodInDays.setValue(null, { emitEvent: false });
    this.syncCashbackForAmount(rule);
  }

  maxValueLabel(rule: AbstractControl) {
    const type = rule.get('validityType').value || 'earnings';
    if (type === 'rides') {
      return 'Ride target';
    }
    return 'Max ' + type.charAt(0).toUpperCase() + type.slice(1);
  }

  isZeroAmount(rule: AbstractControl) {
    const amount = rule.get('amount').value;
    return amount !== null && amount !== '' && Number(amount) === 0;
  }

  syncCashbackForAmount(rule: AbstractControl) {
    const cashback = rule.get('cashbackType');
    if (this.isZeroAmount(rule) || this.isPaidTargetValidity(rule)) {
      cashback.setValue('none', { emitEvent: false });
      rule.get('cashbackMinValue').setValue(null, { emitEvent: false });
      cashback.disable({ emitEvent: false });
      return;
    }
    cashback.enable({ emitEvent: false });
  }

  rejectNonPositiveAmount(event: Event, control) {
    const input = event.target as HTMLInputElement;
    if (input.value === '' || input.value == null) {
      return;
    }
    if (Number(input.value) <= 0) {
      control.setValue(null);
      input.value = '';
    }
  }

  formValue(name: string) {
    return this.postpaidFormGroup.get(name).value;
  }

  serviceLabel(item) {
    return item.name + (item.active ? '' : ' (inactive)');
  }

  get includedCategoryServicesText() {
    if (!this.context || !this.context.categoryServices) {
      return '';
    }
    const excludedIds = {};
    (this.formValue('excludedServices') || []).forEach(service => {
      excludedIds[service.id] = true;
    });
    return this.context.categoryServices
      .filter(service => !excludedIds[service.id])
      .map(service => this.serviceLabel(service))
      .join(', ');
  }

  blockInvalidNumberKeys(event: KeyboardEvent) {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  onCashbackTypeChange(rule: AbstractControl) {
    if (rule.get('cashbackType').value !== 'rides') {
      rule.get('cashbackMinValue').setValue(null);
    }
  }

  errorMessage(message: string) {
    this.toasterService.showToaster(new Toaster({
      type: ToasterType.WARNING,
      message
    }));
  }

  validateField(fieldName: string, message: string, group: AbstractControl = this.postpaidFormGroup) {
    const value = group.get(fieldName).value;
    if (value === null || value === undefined || value === '') {
      this.errorMessage(message);
      throw new Error(message);
    }
    return value;
  }

  buildValidity(rule: AbstractControl) {
    const validityType = this.validateField('validityType', 'Validity type is required', rule);
    if (validityType === 'unlimited' && this.isFemaleGender) {
      return [{ type: 'unlimited', maxValue: null }];
    }
    if (validityType === 'unlimited') {
      return [{ type: 'unlimited' }];
    }
    const maxValue = this.validateField('maxValue', 'Max value is required', rule);
    if (Number(maxValue) <= 0) {
      this.errorMessage('Max value must be greater than zero');
      throw new Error('Max value must be greater than zero');
    }
    return [{ type: validityType, maxValue: Number(maxValue) }];
  }

  buildCashback(rule: AbstractControl, amount: number) {
    const cashbackType = rule.get('cashbackType').value || 'none';
    if (cashbackType !== 'rides') {
      return [];
    }
    const minValue = this.validateField('cashbackMinValue', 'Rides min value for cashback is required', rule);
    if (Number(minValue) <= 0) {
      this.errorMessage('Rides min value for cashback must be greater than zero');
      throw new Error('Rides min value for cashback must be greater than zero');
    }
    return [{
      cashbackAmount: amount,
      minValue: Number(minValue),
      type: 'rides'
    }];
  }

  periodInDaysForRule(rule: AbstractControl) {
    if (!this.isPaidUnlimited(rule)) {
      return undefined;
    }
    const periodInDays = this.validateField('periodInDays', 'Period in days is required', rule);
    if (Number(periodInDays) <= 0) {
      this.errorMessage('Period in days must be greater than zero');
      throw new Error('Period in days must be greater than zero');
    }
    return Number(periodInDays);
  }

  buildRule(rule: AbstractControl) {
    const amount = Number(this.validateField('amount', 'Amount to be deducted is required', rule));
    if (amount < 0) {
      this.errorMessage('Amount to be deducted must be zero or more');
      throw new Error('Amount to be deducted must be zero or more');
    }
    if (this.isPaidTargetValidity(rule) && amount <= 0) {
      this.errorMessage('Amount to be deducted must be greater than zero');
      throw new Error('Amount to be deducted must be greater than zero');
    }
    const periodInDays = this.periodInDaysForRule(rule);
    return {
      cashbackValidity: this.buildCashback(rule, amount),
      ruleAmount: amount,
      ...(periodInDays != null ? { periodInDays } : {}),
      validity: this.buildValidity(rule)
    };
  }

  confirmMessageForTargetSegment() {
    if (this.isCityWideSegment) {
      return 'Creating this plan will deactivate existing active postpaid plans for the same city, mode, and target segment (entire city or female gender). Continue?';
    }
    return 'Create this postpaid plan?';
  }

  buildTargetSegmentPayload() {
    const segment = this.formValue('targetSegment') || 'entireCity';
    let priority = 0;
    let userSelectors: string[] = [];
    let userSelectorName: string = null;
    let gender = null;
    let isAutoAssignedToHH = false;
    let endTime: number = null;

    if (segment === 'femaleGender') {
      gender = 1;
    } else if (segment === 'userSelectors') {
      const selector = this.validateField('userSelector', 'User selector is required');
      priority = Number(this.validateField('priority', 'Priority is required'));
      if (priority <= 0) {
        this.errorMessage('Priority must be greater than zero');
        throw new Error('Priority must be greater than zero');
      }
      endTime = moment(this.validateField('endDate', 'End time is required').getTime()).unix();
      userSelectors = [selector.id];
      userSelectorName = selector.name;
    } else if (segment === 'autoAssign') {
      isAutoAssignedToHH = true;
    }

    return { priority, userSelectors, userSelectorName, gender, isAutoAssignedToHH, endTime };
  }

  async hasOverlappingPriorityRules(segmentPayload, startTime: number, categoryServices) {
    if (!this.showUserSelector) {
      return false;
    }
    const overlapping: any = await this.subsService.getRules({
      filters: {
        type: 'POSTPAID',
        serviceDetails: categoryServices.map(service => service.serviceDetailId),
        cities: [this.context.city.id],
        mode: this.context.mode.id,
        startTime,
        endTime: segmentPayload.endTime,
        active: true,
        isAutoAssignedToHH: false,
        priorities: [segmentPayload.priority]
      },
      pagination: { page: 1, pageSize: this.maxRulesAtOnce }
    }).toPromise();
    if (!overlapping || !overlapping.length) {
      return false;
    }
    this.alertService.open({
      title: 'Overlapping Subscription Rules with same priority',
      message: 'Please create a Subscription Rule with other priority to proceed',
      links: overlapping.map(rule => ({
        label: `${rule.ruleName} (priority ${rule.priority})`,
        url: `/subscription/view/${rule.id}`
      }))
    });
    return true;
  }

  async createPostpaidRule() {
    this.alertService.open({
      title: 'Confirm',
      message: this.confirmMessageForTargetSegment(),
      confirmText: 'Yes, Proceed'
    });
    const confirmed = await this.alertService.confirmed().toPromise();
    if (!confirmed) {
      return;
    }
    this.loader.openLoading();
    try {
      if (!this.context) {
        throw new Error('Select state, city, mode and category first');
      }
      const ruleName = this.validateField('ruleName', 'Rule name is required');
      const startTime = moment(this.validateField('startDate', 'Start time is required').getTime()).unix();
      const categoryServices = this.context.categoryServices || [];
      if (!categoryServices.length) {
        this.errorMessage('No services found in this category');
        throw new Error('No services found in this category');
      }
      const excludedServices = (this.formValue('excludedServices') || []).map(service => service.id);
      const segmentPayload = this.buildTargetSegmentPayload();

      const exclusiveOfTax = !!(this.context && this.context.exclusiveOfTax);
      const rules = this.rules.controls.map(rule => this.buildRule(rule));
      if (await this.hasOverlappingPriorityRules(segmentPayload, startTime, categoryServices)) {
        return;
      }
      const payload = {
        type: 'POSTPAID',
        rules: rules.map(rule => ({
          ...rule,
          categoryLevelApplicability: {
            categories: [{
              allowedServices: [],
              excludedServices,
              id: this.context.category
            }]
          },
          city: this.context.city.id,
          excludeIncentiveTypes: [],
          exclusiveOfTax,
          isAutoAssignedToHH: segmentPayload.isAutoAssignedToHH,
          isAutoAssignedToMC: false,
          isDefaultSubscription: this.isCityWideSegment,
          gender: segmentPayload.gender,
          isRecommended: false,
          mode: this.context.mode.id,
          priority: segmentPayload.priority,
          rule: [{
            operator: '=',
            type: 'flat',
            value: 0,
            variable: 'commission'
          }],
          ruleName,
          ruleTitle: this.formValue('ruleTitle') || undefined,
          ruleType: this.postpaidFormGroup.get('ruleType').value || this.eligibleRuleTypes[0],
          serviceDetails: categoryServices.map(service => service.serviceDetailId),
          services: categoryServices.map(service => service.id),
          startTime,
          ...(segmentPayload.endTime != null ? { endTime: segmentPayload.endTime } : {}),
          suspendServicesPostExpiry: false,
          userSelectors: segmentPayload.userSelectors,
          userSelectorName: segmentPayload.userSelectorName || undefined
        }))
      };

      const resp: any = await this.subsService.createSubscriptionRules(payload).toPromise();
      this.alertService.open(subscriptionCreateSuccessAlert(resp, { configRequestKind: 'subscription' }));
    } catch (error) {
      this.alertService.open(subscriptionOperationFailureAlert(error));
    } finally {
      this.loader.closeLoading();
    }
  }
}
